import { useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials } from '../../../mocks/testimonials';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const { ref, isVisible } = useScrollAnimation();
  const featured = testimonials[active] || testimonials[0];

  return (
    <section className="py-28 bg-white overflow-hidden" id="reviews">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-4">
            <div className="h-px w-8 bg-brand-amber" />
            Customer Reviews
            <div className="h-px w-8 bg-brand-amber" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-brand-dark mb-3">
            Real Reviews from Real Jacksonville Homeowners
          </h2>
          <div className="flex items-center justify-center gap-1.5 mt-4">
            {[1,2,3,4,5].map((s) => (
              <div key={s} className="w-5 h-5 flex items-center justify-center">
                <i className="ri-star-fill text-brand-amber" />
              </div>
            ))}
            <span className="ml-2 text-brand-dark font-bold text-xl">5.0</span>
            <span className="text-brand-warm-gray ml-2 text-sm">based on 500+ verified reviews</span>
          </div>
        </div>

        {/* Featured + Supporting layout */}
        <div className="flex flex-col xl:flex-row gap-8 mb-8">
          {/* Featured Testimonial — left, large */}
          <div className="xl:w-5/12">
            <div className="h-full bg-brand-dark rounded-3xl p-10 flex flex-col justify-between relative overflow-hidden">
              {/* Background quote mark */}
              <div className="absolute -top-4 -right-4 text-[140px] leading-none text-white/5 font-serif select-none pointer-events-none">
                &ldquo;
              </div>

              <div>
                <div className="flex gap-1 mb-6">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-star-fill text-brand-amber" />
                    </div>
                  ))}
                </div>
                <p className="text-white/90 text-lg leading-relaxed mb-8 font-light">
                  &ldquo;{featured.text}&rdquo;
                </p>
              </div>

              <div>
                <div className="h-px bg-white/10 mb-6" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center bg-brand-amber rounded-full flex-shrink-0">
                      <span className="text-white font-bold">{featured.initials}</span>
                    </div>
                    <div>
                      <div className="text-white font-semibold">{featured.name}</div>
                      <div className="text-white/50 text-sm">{featured.location}</div>
                    </div>
                  </div>
                  <div className="bg-brand-amber/20 text-amber-300 text-xs font-semibold px-3 py-1.5 rounded-full border border-brand-amber/30 whitespace-nowrap">
                    {featured.service}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Supporting cards grid — right */}
          <div className="xl:w-7/12 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {testimonials.slice(0, 4).map((t, i) => (
              <div
                key={t.id}
                onClick={() => setActive(i)}
                className={`p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
                  active === i
                    ? 'border-brand-amber bg-amber-50'
                    : 'border-stone-100 bg-stone-50 hover:border-brand-amber/40 hover:bg-amber-50/30'
                }`}
              >
                <div className="flex gap-0.5 mb-3">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-brand-amber text-xs" />
                    </div>
                  ))}
                </div>
                <p className="text-brand-light text-sm leading-relaxed mb-4 line-clamp-3">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                      <span className="text-brand-amber font-bold text-xs">{t.initials}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-brand-dark font-semibold text-xs truncate">{t.name}</div>
                      <div className="text-brand-warm-gray text-xs">{t.location}</div>
                    </div>
                  </div>
                  <span className="text-xs text-brand-warm-gray bg-stone-200 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">{t.service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* More reviews row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {testimonials.slice(4, 7).map((t) => (
            <div
              key={t.id}
              className="p-6 bg-stone-50 border border-stone-100 rounded-2xl hover:border-brand-amber/30 hover:bg-amber-50/30 transition-all duration-200"
            >
              <div className="flex gap-0.5 mb-3">
                {[1,2,3,4,5].map((s) => (
                  <div key={s} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-brand-amber text-xs" />
                  </div>
                ))}
              </div>
              <p className="text-brand-light text-sm leading-relaxed mb-4 line-clamp-4">
                &ldquo;{t.text}&rdquo;
              </p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                  <span className="text-brand-amber font-bold text-xs">{t.initials}</span>
                </div>
                <div>
                  <div className="text-brand-dark font-semibold text-xs">{t.name}</div>
                  <div className="text-brand-warm-gray text-xs">{t.location}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3.5 px-9 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
          >
            Read All 500+ Reviews
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
